import { forwardRef } from 'react';
import { Alert, AlertProps, AlertTitle } from '@mui/material';
import { CustomContentProps, SnackbarContent, SnackbarProvider, useSnackbar } from 'notistack';

import { useSnackbarInterceptor } from './useSnackbarInterceptor';

interface SnackbarProps {
  displayErrors?: boolean;
  displaySuccess?: boolean;
}

const Snackbar = ({ displayErrors, displaySuccess }: SnackbarProps) => {
  useSnackbarInterceptor({ displayErrors, displaySuccess });

  return null;
};

interface SnackbarAlertProps extends CustomContentProps {
  title?: string;
  AlertProps?: AlertProps;
}

const SnackbarAlertComponent = (props: SnackbarAlertProps, ref: React.Ref<HTMLDivElement>) => {
  const { id, message, variant, title, AlertProps, className, action } = props;

  const { closeSnackbar } = useSnackbar();

  return (
    <SnackbarContent ref={ref} role="alert" className={className}>
      <Alert
        variant="filled"
        severity={variant !== 'default' ? variant : undefined}
        onClose={() => closeSnackbar(id)}
        {...AlertProps}
        action={typeof action === 'function' ? action(id) : action}
        sx={{ width: '100%', ...AlertProps?.sx }}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {message}
      </Alert>
    </SnackbarContent>
  );
};

const SnackbarAlert = Object.assign(forwardRef(SnackbarAlertComponent), {
  displayName: 'SnackbarAlert'
});

interface SnackbarWithProviderProps {
  children: React.ReactNode;
  displayErrors?: boolean;
  displaySuccess?: boolean;
}

const SnackbarWithProvider = ({
  children,
  displayErrors = true,
  displaySuccess = false
}: SnackbarWithProviderProps) => {
  return (
    <SnackbarProvider
      Components={{
        default: SnackbarAlert,
        success: SnackbarAlert,
        error: SnackbarAlert,
        info: SnackbarAlert,
        warning: SnackbarAlert
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      maxSnack={5}
    >
      <Snackbar displayErrors={displayErrors} displaySuccess={displaySuccess} />
      {children}
    </SnackbarProvider>
  );
};

export default SnackbarWithProvider;
